import React, { useState, useEffect } from 'react';
import authService from '../services/authService';
import configService from '../services/configService';
import logo from '../Mantle Logo.png';

// Provider marks, drawn rather than fetched — a login page must not depend on a
// remote asset it cannot reach on a locked-down corporate network.
const ProviderMark = ({ provider }) => {
  if (provider === 'entra') {
    return (
      <svg viewBox="0 0 21 21" className="w-4 h-4" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
      </svg>
    );
  }
  if (provider === 'google') {
    return (
      <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
        <path fill="#4285F4" d="M45 24c0-1.6-.1-2.7-.4-4H24v7.5h12c-.2 2-1.5 5-4.4 7l6.7 5.2C42.2 36.2 45 30.6 45 24z" />
        <path fill="#34A853" d="M24 46c5.9 0 10.9-2 14.5-5.3l-6.9-5.4c-1.9 1.3-4.4 2.2-7.6 2.2-5.8 0-10.7-3.9-12.5-9.1l-7.1 5.5C8 40.4 15.4 46 24 46z" />
        <path fill="#FBBC05" d="M11.5 28.4a13.3 13.3 0 010-8.8l-7.1-5.5a22 22 0 000 19.8l7.1-5.5z" />
        <path fill="#EA4335" d="M24 9.5c3.2 0 5.4 1.4 6.7 2.6l5.9-5.8C33 2.9 29.9 1 24 1 15.4 1 8 6.6 4.4 14.1l7.1 5.5C13.3 13.4 18.2 9.5 24 9.5z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
};

const PROVIDER_NAMES = {
  entra: 'Microsoft', google: 'Google', okta: 'Okta', oidc: 'single sign-on', saml: 'single sign-on',
};

const Login = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // What the login page is allowed to know before anyone has authenticated:
  // whether there is an SSO button to draw and whether the password form should
  // still be offered. Nothing about the issuer, the client or the domains.
  const [sso, setSso] = useState({ enabled: false, enforced: false, allow_local_admin: true, handshake_ready: false });
  const [showLocal, setShowLocal] = useState(false);

  // Force reading directly from the config file
  const dbName = window.MANTLE_CONFIG?.TENANT_DB;

  useEffect(() => {
    let live = true;
    configService.getPublicSso()
      .then((res) => { if (live && res?.data) setSso(res.data); })
      // A login page renders with or without this. Failing to reach config
      // leaves the password form, which is the safe direction to fail in.
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const startSso = () => {
    if (!sso.handshake_ready) {
      setError('Single sign-on is configured but not yet switched on. Use your Mantle password for now.');
      return;
    }
    window.location.href = '/api/auth/sso/start';
  };

  // Enforced SSO hides the password form, but never removes it entirely: the
  // administrator escape is what gets the tenant back in when the identity
  // provider is unreachable.
  const localHidden = sso.enabled && sso.enforced && !showLocal;
  const ssoLabel = sso.display_name || `Sign in with ${PROVIDER_NAMES[sso.provider] || 'single sign-on'}`;

  const handleLogin = async (e) => {
    e.preventDefault(); 
    
    // Fail fast if the deployment wasn't configured properly
    if (!dbName) {
      setError("Missing workspace context. Please check your server deployment configuration.");
      return;
    }

    if (username !== "" && password !== "") {
      try {
        const response = await authService.login(username, password);
        
        if (response.data && response.data.userFound) {
          setError('');
          onLoginSuccess();
        } else {
          setError(response.data?.err || 'Invalid username or password.');
        }
      } catch (err) {
        const errMsg = err.response?.data?.err || err.message;
        setError("Login Failed: " + errMsg);
      }
    } else {
      setError('Invalid username or password.');
    }
  };

  return (
    <div className="flex justify-center items-center h-screen bg-slate-900 font-sans">
      <div className="w-full max-w-[500px] bg-white p-10 rounded-lg shadow-md border border-slate-200">
        
        {/* Branding */}
        <div className="text-center mb-8">
          <img
            src={logo}
            alt="Mantle Logo"
            className="h-16 w-auto mx-auto"
          />
          <p className="mt-1.5 text-slate-500 text-sm">
            Please sign in to your workspace
          </p>
        </div>

        {/* SSO first. It is the route most people should take when it is on,
            and putting it under the password form trains everyone to ignore it. */}
        {sso.enabled && (
          <>
            <button
              type="button"
              onClick={startSso}
              className="w-full flex items-center justify-center gap-2.5 p-3 rounded border border-slate-300 bg-white text-slate-800 text-base font-semibold cursor-pointer transition-colors hover:bg-slate-50"
            >
              <ProviderMark provider={sso.provider} />
              {ssoLabel}
            </button>

            {!sso.handshake_ready && (
              <p className="mt-2 text-center text-[0.75rem] text-amber-700">
                Configured, but sign-in is not switched on yet.
              </p>
            )}

            {!localHidden && (
              <div className="flex items-center gap-3 my-5">
                <span className="flex-1 h-px bg-slate-200" />
                <span className="text-[0.75rem] text-slate-400">or</span>
                <span className="flex-1 h-px bg-slate-200" />
              </div>
            )}
          </>
        )}

        {/* The way back in when the identity provider is not answering. Quiet,
            but never absent. */}
        {localHidden && (
          <div className="mt-5 text-center">
            <p className="m-0 text-[0.8rem] text-slate-500">
              Your organisation requires single sign-on.
            </p>
            {sso.allow_local_admin && (
              <button
                type="button"
                onClick={() => setShowLocal(true)}
                className="mt-1.5 text-[0.75rem] text-slate-500 underline hover:text-slate-700"
              >
                Administrator sign-in
              </button>
            )}
            {error && <div className="mt-3 text-red-600 text-[0.85rem]">{error}</div>}
          </div>
        )}

        <form onSubmit={handleLogin} className={`flex flex-col gap-5 ${localHidden ? 'hidden' : ''}`}>

          {/* Username Input */}
          <div className="flex flex-col gap-2">
            <label className="text-[0.85rem] font-semibold text-slate-600" htmlFor="uname_email">
              Username or Email Address
            </label>
            <input 
              name="uname_email" 
              id="uname_email"
              type="text" 
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="p-2.5 rounded border border-slate-300 text-base outline-none transition-shadow focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
              placeholder="Enter your username or email address"
            />
          </div>
 
          {/* Password Input */}
          <div className="flex flex-col gap-2">
            <label className="text-[0.85rem] font-semibold text-slate-600" htmlFor="upasswd">
              Password
            </label>
            <input 
              name="upasswd" 
              id="upasswd"
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="p-2.5 rounded border border-slate-300 text-base outline-none transition-shadow focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
              placeholder="Enter your password"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-red-600 text-[0.85rem] text-center">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button 
            type="submit"
            className="mt-2.5 p-3 bg-slate-900 text-white border-none rounded text-base font-semibold cursor-pointer transition-colors hover:bg-slate-800"
          >
            Sign In
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
